import type { Artist } from "@/types";

// Schema.org JSON-LD builders. Every value here must mirror content that is
// visible on the page it is rendered in — structured data describing hidden
// content is a search-engine penalty risk and misleads AI answer engines.

export const SITE_URL = "https://studiocomm.art";
const ORGANIZATION_ID = `${SITE_URL}/#organization`;

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildFaqPage(items: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

/** "$65" / "65" / 65 → "65.00"; anything unparseable (e.g. "Ask") → null. */
export function parseUsdPrice(value: string | number): string | null {
  const match = String(value).replace(/,/g, "").match(/\d+(\.\d+)?/);
  return match ? Number(match[0]).toFixed(2) : null;
}

export function buildArtistGraph(artist: Artist) {
  const pageUrl = `${SITE_URL}/artist/${artist.slug}`;
  const personId = `${pageUrl}#artist`;

  const offers = Object.entries(artist.prices ?? {}).flatMap(([tier, raw]) => {
    const price = parseUsdPrice(raw);
    if (!price) return [];
    return [
      {
        "@type": "Offer",
        name: `${tier} commission by ${artist.name}`,
        price,
        priceCurrency: "USD",
        availability:
          artist.status === "open" ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
        url: pageUrl,
        seller: { "@id": ORGANIZATION_ID },
      },
    ];
  });

  const sameAs = [artist.links?.twitter, artist.links?.website].filter(
    (link): link is string => Boolean(link)
  );

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": personId,
        name: artist.name,
        jobTitle: "Illustrator",
        description: artist.specialty
          ? `${artist.specialty} — hand-drawn art commissions at StudioComm. No AI.`
          : "Hand-drawn art commissions at StudioComm. No AI.",
        url: pageUrl,
        ...(artist.profilePicture ? { image: artist.profilePicture } : {}),
        ...(sameAs.length ? { sameAs } : {}),
        ...(artist.tags?.length ? { knowsAbout: artist.tags } : {}),
        worksFor: { "@id": ORGANIZATION_ID },
        ...(offers.length ? { makesOffer: offers } : {}),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Artists", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: artist.name, item: pageUrl },
        ],
      },
    ],
  };
}
